<template>
  <div class="cbz-list">
    <div v-if="notice" class="cbz-notice" role="alert">
      <span class="cbz-notice__text">{{ notice }}</span>
      <button class="cbz-notice__close" @click="notice = ''"><span class="mdi mdi-close"></span></button>
    </div>
    <div v-if="selectedIds.size >= 1" class="cbz-list__batch-bar">
      <span class="cbz-list__batch-count">{{ selectedIds.size }} cuenta{{ selectedIds.size === 1 ? '' : 's' }} seleccionada{{ selectedIds.size === 1 ? '' : 's' }}</span>
      <button class="cbz-list__batch-action" @click="onBatchSendReminder">Enviar recordatorio masivo</button>
      <button class="cbz-list__batch-action" @click="onStub('Generar estados de cuenta')">Generar estados de cuenta</button>
      <button class="cbz-list__batch-action" @click="onBatchMarkManaged">Marcar como gestionados</button>
      <button class="cbz-list__batch-action" @click="onExportCsv">Exportar selección</button>
      <button class="cbz-list__batch-clear" @click="clearSelection"><span class="mdi mdi-close"></span></button>
    </div>

    <div class="cbz-list__col-header">
      <div class="cbz-list__col cbz-list__col--status">
        <button class="cbz-list__filter-btn" @click="statusFilterOpen = !statusFilterOpen"><span class="mdi mdi-filter-variant"></span></button>
        <div v-if="statusFilterOpen" class="cbz-list__filter-dropdown">
          <label v-for="opt in STATUS_FILTER_OPTIONS" :key="opt.value" class="cbz-list__filter-option">
            <input type="checkbox" :checked="statusFilter.has(opt.value)" @change="toggleStatusFilter(opt.value)" />
            <span class="cbz-list__filter-dot" :style="{ background: opt.color }"></span>
            {{ opt.label }}
          </label>
        </div>
      </div>
      <button class="cbz-list__col cbz-list__col--sortable cbz-list__col--tenant" @click="toggleSort('tenant')">Inquilino y nave</button>
      <button class="cbz-list__col cbz-list__col--sortable cbz-list__col--charges" @click="toggleSort('charges')">Cargos del mes</button>
      <button class="cbz-list__col cbz-list__col--sortable cbz-list__col--paid" @click="toggleSort('paid')">Abonado</button>
      <button class="cbz-list__col cbz-list__col--sortable cbz-list__col--pending" @click="toggleSort('pending')">Saldo pendiente</button>
      <button class="cbz-list__col cbz-list__col--sortable cbz-list__col--due" @click="toggleSort('due')">Vencimiento</button>
      <div class="cbz-list__col cbz-list__col--lastpay">Último pago</div>
      <div class="cbz-list__col cbz-list__col--actions"></div>
    </div>

    <div class="cbz-list__body">
      <div
        v-for="(row, index) in sortedRows"
        :key="row.contractId"
        class="cbz-list__row"
        :class="{
          'cbz-list__row--even': index % 2 === 1,
          'cbz-list__row--overdue-severe': row.statusColor === 'red',
          'cbz-list__row--selected': selectedIds.has(row.contractId),
        }"
        @click="onRowClick(row)"
      >
        <div class="cbz-list__col cbz-list__col--status" @click.stop="toggleSelect(row.contractId)">
          <template v-if="!selectedIds.size">
            <span class="cbz-list__dot" :class="`cbz-list__dot--${row.statusColor}`"></span>
          </template>
          <input v-else type="checkbox" :checked="selectedIds.has(row.contractId)" @click.stop="toggleSelect(row.contractId)" />
        </div>

        <div class="cbz-list__col cbz-list__col--tenant" @mouseenter="hoveredTenantId = row.contractId" @mouseleave="hoveredTenantId = null">
          <div class="cbz-list__tenant-name-row">
            <span class="cbz-list__tenant-name">{{ row.tenantName }}</span>
            <span v-if="row.hasRecentReminder" class="mdi mdi-email-outline cbz-list__reminder-icon" :title="`Recordatorio enviado ${formatDate(row.reminderInfo?.date)}`"></span>
          </div>
          <span class="cbz-list__plot-tag">{{ row.plotName }}</span>
          <div v-if="hoveredTenantId === row.contractId && (row.razonSocial || row.rfc)" class="cbz-list__tooltip">
            <div v-if="row.razonSocial">{{ row.razonSocial }}</div>
            <div v-if="row.rfc" class="cbz-list__tooltip-rfc">RFC: {{ row.rfc }}</div>
          </div>
        </div>

        <div class="cbz-list__col cbz-list__col--charges" @mouseenter="hoveredChargesId = row.contractId" @mouseleave="hoveredChargesId = null">
          {{ formatNumber(row.totalCurrent) }}
          <div v-if="hoveredChargesId === row.contractId" class="cbz-list__tooltip cbz-list__tooltip--breakdown">
            <div>Renta: {{ formatCurrency(row.byType.RENT) }}</div>
            <div>Servicios: {{ formatCurrency(row.byType.SERVICE) }}</div>
            <div>Adicionales: {{ formatCurrency(row.byType.OTHER) }}</div>
          </div>
        </div>

        <div class="cbz-list__col cbz-list__col--paid" :class="paidColorClass(row)">
          {{ row.paidCurrent === 0 ? '—' : formatNumber(row.paidCurrent) }}
        </div>

        <div class="cbz-list__col cbz-list__col--pending">
          <div class="cbz-list__pending-main" :class="row.pendingCurrent === 0 ? 'cbz-list__pending-main--zero' : `cbz-list__pending-main--${row.statusColor}`">
            {{ row.pendingCurrent === 0 ? '$0' : formatCurrency(row.pendingCurrent) }}
          </div>
          <div v-if="row.usdPending > 0" class="cbz-list__pending-prior">+ US$ {{ Math.round(row.usdPending).toLocaleString('es-MX') }} en dólares</div>
          <div v-if="row.priorPendingTotal > 0 && !accumulated" class="cbz-list__pending-prior">+ {{ formatCompact(row.priorPendingTotal) }} de meses anteriores</div>
        </div>

        <div class="cbz-list__col cbz-list__col--due" :class="dueColorClass(row)">
          <template v-if="row.overdueDays > 0">Vencido {{ row.overdueDays }}d</template>
          <template v-else-if="row.mainDueDate">{{ formatDueDateShort(row.mainDueDate) }}</template>
          <template v-else>—</template>
        </div>

        <div class="cbz-list__col cbz-list__col--lastpay">
          <span>{{ row.lastPaymentDate ? formatRelative(row.lastPaymentDate) : '—' }}</span>
          <span v-if="row.commitmentDate" class="cbz-list__commitment">Prometió pagar el {{ formatDateShort(row.commitmentDate) }}</span>
        </div> 
       <div class="cbz-list__col cbz-list__col--actions" @click.stop>
          <button class="cbz-list__menu-btn" @click="openActionMenu(row, $event)"><span class="mdi mdi-dots-vertical"></span></button>
        </div>
      </div>

      <Teleport to="body">
        <div v-if="actionMenuRow" class="cbz-list__action-menu" :style="actionMenuPos">
          <button v-for="a in getRowActions(actionMenuRow)" :key="a.label" class="cbz-list__action-item" @click="onRowAction(actionMenuRow, a)">
            <span v-if="a.priority" class="cbz-list__action-dot"></span>
            {{ a.label }}
          </button>
        </div>
      </Teleport>
    </div>

    <div class="cbz-list__footer">
      <span class="cbz-list__footer-count">{{ footerText }}</span>
      <div class="cbz-list__footer-right">
        <span class="cbz-list__stat" tabindex="0">
          <span class="cbz-list__stat-label">Cargos</span>
          <span class="cbz-list__footer-total">{{ formatCurrency(footer.totalCargos) }}</span>
          <span class="cbz-list__stat-tip" role="tooltip"><strong>Total de cargos</strong>Todo lo que se cobra en el periodo a los inquilinos con contrato vigente, sin restar lo ya pagado.</span>
        </span>
        <span class="cbz-list__stat" tabindex="0">
          <span class="cbz-list__stat-label">Abonado</span>
          <span class="cbz-list__footer-total cbz-list__footer-total--green">{{ formatCurrency(footer.totalAbonado) }}</span>
          <span class="cbz-list__stat-tip" role="tooltip"><strong>Total abonado</strong>Lo que esos inquilinos ya han pagado de los cargos del periodo.</span>
        </span>
        <span v-if="accumulated && footer.totalAnteriores > 0" class="cbz-list__stat" tabindex="0">
          <span class="cbz-list__stat-label">+ Anteriores</span>
          <span class="cbz-list__footer-total cbz-list__footer-total--amber">{{ formatCurrency(footer.totalAnteriores) }}</span>
          <span class="cbz-list__stat-tip" role="tooltip"><strong>Saldo de meses anteriores</strong>En modo acumulado, lo que aún se debe de periodos previos se suma al pendiente.</span>
        </span>
        <span v-if="footer.usdPendiente > 0" class="cbz-list__stat" tabindex="0">
          <span class="cbz-list__stat-label">USD</span>
          <span class="cbz-list__footer-total cbz-list__footer-total--amber">US$ {{ Math.round(footer.usdPendiente).toLocaleString('es-MX') }}</span>
          <span class="cbz-list__stat-tip" role="tooltip"><strong>Pendiente en dólares</strong>Cargos en dólares con saldo. No se suman a los totales en pesos.</span>
        </span>
        <span class="cbz-list__stat" tabindex="0">
          <span class="cbz-list__stat-label">Pendiente</span>
          <span class="cbz-list__footer-total cbz-list__footer-total--amber">{{ formatCurrency(footer.totalPendiente) }}</span>
          <span class="cbz-list__stat-tip" role="tooltip"><strong>Total pendiente</strong>Cargos − Abonado (+ Anteriores en modo acumulado). Es la misma cifra que la tarjeta «Total por cobrar».</span>
        </span>
        <span class="cbz-list__stat" tabindex="0">
          <span class="cbz-list__footer-total" :class="footer.vencidosCount > 0 ? 'cbz-list__footer-total--red' : 'cbz-list__footer-total--gray'">{{ footer.vencidosCount }} vencidos</span>
          <span class="cbz-list__stat-tip" role="tooltip"><strong>Inquilinos con atraso</strong>Cuántos inquilinos tienen al menos un cargo vencido, de este periodo o de anteriores.</span>
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { getAccountsList, sendReminder, addManagementNote } from '@/services/charges'

const props = defineProps({
  parkId: { type: String, required: true },
  period: { type: String, required: true },
  accumulated: { type: Boolean, default: false },
  activeTab: { type: String, default: 'todos' },
})

const emit = defineEmits(['open-account', 'reload-metrics', 'generate-statement'])

const STATUS_FILTER_OPTIONS = [
  { value: 'green', label: 'Al corriente', color: '#10b981' },
  { value: 'blue', label: 'Pendiente en plazo', color: '#3b82f6' },
  { value: 'amber', label: 'Parcial', color: '#f0a500' },
  { value: 'orange', label: 'Vencido <30d', color: '#f97316' },
  { value: 'red', label: 'Vencido >30d', color: '#e84545' },
]

const TAB_STATUS_MAP = {
  pendientes: ['blue', 'amber'],
  alCorriente: ['green'],
  vencidos: ['orange', 'red'],
}

const rows = ref([])
const footer = ref({ totalCargos: 0, totalAbonado: 0, totalPendiente: 0, vencidosCount: 0 })

async function load() {
  const result = await getAccountsList(props.parkId, props.period, props.accumulated)
  rows.value = result.rows
  footer.value = result.footer
  emit('tickets-updated', rows.value) // agrega esta línea, y 'tickets-updated' a los defineEmits
}
onMounted(load)
watch([() => props.period, () => props.accumulated], load)
defineExpose({
  reload: load,
  getGenerationContext: () => ({
    selectedRows: sortedRows.value.filter((r) => selectedIds.value.has(r.contractId)),
    allFilteredRows: sortedRows.value,
  }),
})

const statusFilterOpen = ref(false)
const statusFilter = ref(new Set())
function toggleStatusFilter(v) {
  if (statusFilter.value.has(v)) statusFilter.value.delete(v)
  else statusFilter.value.add(v)
  statusFilter.value = new Set(statusFilter.value)
}

const tabFilteredRows = computed(() => {
  if (props.activeTab === 'todos') return rows.value
  // Pendientes: cualquiera con saldo que aún no vence, aunque además tenga algo vencido (aparece en ambas pestañas).
  if (props.activeTab === 'pendientes') return rows.value.filter((r) => r.hasPending)
  const allowed = TAB_STATUS_MAP[props.activeTab]
  return allowed ? rows.value.filter((r) => allowed.includes(r.statusColor)) : rows.value
})

const filteredRows = computed(() => {
  if (statusFilter.value.size === 0) return tabFilteredRows.value
  return tabFilteredRows.value.filter((r) => statusFilter.value.has(r.statusColor))
})

const sortKey = ref(null)
const sortDir = ref('asc')
function toggleSort(key) {
  if (sortKey.value !== key) { sortKey.value = key; sortDir.value = 'asc' }
  else if (sortDir.value === 'asc') sortDir.value = 'desc'
  else sortKey.value = null
}
function sortValue(r, key) {
  if (key === 'tenant') return r.tenantName.toLowerCase()
  if (key === 'charges') return r.totalCurrent
  if (key === 'paid') return r.paidCurrent
  if (key === 'pending') return r.pendingCurrent
  if (key === 'due') return r.mainDueDate ? new Date(r.mainDueDate).getTime() : Infinity
  return ''
}
const sortedRows = computed(() => {
  if (!sortKey.value) return filteredRows.value
  const list = [...filteredRows.value]
  list.sort((a, b) => {
    const va = sortValue(a, sortKey.value), vb = sortValue(b, sortKey.value)
    if (va < vb) return sortDir.value === 'asc' ? -1 : 1
    if (va > vb) return sortDir.value === 'asc' ? 1 : -1
    return 0
  })
  return list
})

// --- Selección ---
const selectedIds = ref(new Set())
function toggleSelect(id) {
  const next = new Set(selectedIds.value)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  selectedIds.value = next
}
function clearSelection() { selectedIds.value = new Set() }

function onRowClick(row) {
  if (selectedIds.value.size > 0) { toggleSelect(row.contractId); return }
  emit('open-account', row.contractId)
}

// --- Batch actions ---

// Envía recordatorios y junta los que fallan (por ejemplo, inquilinos sin correo de cobranza) para avisar al final.
const notice = ref('')
async function sendReminders(contractIds) {
  const failed = []
  let sent = 0
  for (const id of contractIds) {
    try { await sendReminder(id); sent++ }
    catch (err) { failed.push({ id, message: err.message }) }
  }
  if (failed.length) {
    const names = failed.map((f) => rows.value.find((r) => r.contractId === f.id)?.tenantName || 'Cuenta sin nombre')
    notice.value = contractIds.length === 1
      ? failed[0].message
      : `${sent ? `Se enviaron ${sent}. ` : ''}No se pudo enviar a ${failed.length}: ${names.join(', ')}. Revisa que tengan un contacto de pagos con correo.`
  }
}
async function onBatchSendReminder() {
  await sendReminders([...selectedIds.value])
  clearSelection()
  await load()
}
async function onBatchMarkManaged() {
  const text = prompt('Nota de gestión para las cuentas seleccionadas:')
  if (!text) return
  for (const id of selectedIds.value) await addManagementNote(id, text)
  clearSelection()
}
function onExportCsv() {
  const selected = rows.value.filter((r) => selectedIds.value.has(r.contractId))
  const header = 'Inquilino,Nave,Cargos,Abonado,Pendiente,Vencimiento\n'
  const csv = header + selected.map((r) => `"${r.tenantName}","${r.plotName}",${r.totalCurrent},${r.paidCurrent},${r.pendingCurrent},"${r.mainDueDate || ''}"`).join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `cobranza-${props.period}.csv`
  a.click()
  URL.revokeObjectURL(url)
}
function onStub(label) { console.log(`TODO: ${label} — pendiente de construir`) }

// --- Tooltips ---
const hoveredTenantId = ref(null)
const hoveredChargesId = ref(null)

// --- Menú de acciones ---
const actionMenuRow = ref(null)
const actionMenuPos = ref({ top: '0px', left: '0px' })

function openActionMenu(row, event) {
  if (actionMenuRow.value?.contractId === row.contractId) { actionMenuRow.value = null; return }
  const rect = event.currentTarget.getBoundingClientRect()
  actionMenuPos.value = { top: rect.bottom + 4 + 'px', left: rect.right - 220 + 'px' }
  actionMenuRow.value = row
}

function getRowActions(row) {
  if (row.statusColor === 'green') {
    return [
      { label: 'Ver detalle completo', action: 'view-detail' },
      { label: 'Generar estado de cuenta', action: 'generate-statement' },
      { label: 'Ver historial de pagos', action: 'view-history' },
    ]
  }
  if (row.statusColor === 'orange' || row.statusColor === 'red') {
    return [
      { label: 'Registrar pago', action: 'register-payment', priority: true },
      { label: 'Enviar recordatorio de cobro', action: 'send-reminder' },
      { label: 'Ver detalle completo', action: 'view-detail' },
      { label: 'Generar estado de cuenta', action: 'generate-statement' },
      { label: 'Agregar nota de gestión', action: 'add-note' },
      { label: 'Escalar a dirección', action: 'escalate' },
    ]
  }
  return [
    { label: 'Registrar pago', action: 'register-payment' },
    { label: 'Ver detalle completo', action: 'view-detail' },
    { label: 'Generar estado de cuenta', action: 'generate-statement' },
    { label: 'Enviar recordatorio preventivo', action: 'send-reminder' },
  ]
}

async function onRowAction(row, action) {
  actionMenuRow.value = null
  if (action.action === 'send-reminder') {
    await sendReminders([row.contractId])
    await load()
    return
  }
  if (action.action === 'add-note') {
    const text = prompt('Nota de gestión:')
    if (text) await addManagementNote(row.contractId, text)
    return
  }
  if (action.action === 'generate-statement') {
    emit('generate-statement', row.contractId)
    return
  }
  if (action.action === 'view-detail' || action.action === 'register-payment' || action.action === 'view-history') {
    emit('open-account', row.contractId)
    return
  }
  onStub(action.label)
}

function handleOutsideClick(e) {
  if (!e.target.closest('.cbz-list__menu-btn') && !e.target.closest('.cbz-list__action-menu')) actionMenuRow.value = null
  if (!e.target.closest('.cbz-list__filter-btn') && !e.target.closest('.cbz-list__filter-dropdown')) statusFilterOpen.value = false
}
onMounted(() => window.addEventListener('click', handleOutsideClick))
onUnmounted(() => window.removeEventListener('click', handleOutsideClick))

// --- Formato ---
function paidColorClass(r) {
  if (r.paidCurrent === 0) return ''
  const pct = r.totalCurrent === 0 ? 0 : r.paidCurrent / r.totalCurrent
  if (pct >= 1) return 'cbz-list__paid--green'
  if (pct >= 0.5) return 'cbz-list__paid--amber'
  return 'cbz-list__paid--red'
}
function dueColorClass(r) {
  if (r.overdueDays > 0) return 'cbz-list__due--red'
  if (!r.mainDueDate) return ''
  const days = Math.ceil((new Date(r.mainDueDate).getTime() - Date.now()) / (24 * 60 * 60 * 1000))
  if (days < 5) return 'cbz-list__due--red'
  if (days <= 15) return 'cbz-list__due--amber'
  return 'cbz-list__due--navy'
}

const footerText = computed(() => {
  if (selectedIds.value.size > 0) return `${selectedIds.value.size} de ${rows.value.length} seleccionados`
  if (props.activeTab !== 'todos') return `${sortedRows.value.length} inquilinos ${props.activeTab === 'vencidos' ? 'vencidos' : props.activeTab === 'alCorriente' ? 'al corriente' : 'pendientes'}`
  return `${sortedRows.value.length} inquilinos`
})

function formatNumber(n) { return new Intl.NumberFormat('es-MX', { maximumFractionDigits: 0 }).format(n || 0) }
function formatCurrency(n) { return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n || 0) }
function formatCompact(n) { return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0, notation: 'compact' }).format(n || 0) }
function formatDueDateShort(iso) { return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', timeZone: 'UTC' }) }
function formatDateShort(iso) { return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' }) }
function formatDate(iso) { return iso ? new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—' }
function formatRelative(iso) {
  const d = new Date(iso), now = new Date()
  const days = Math.floor((now - d) / (24 * 60 * 60 * 1000))
  if (days === 0) return 'Hoy'
  if (days === 1) return 'Ayer'
  if (days < 7) return `Hace ${days}d`
  return formatDateShort(iso)
}
</script>

<style scoped>
.cbz-list {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  background: var(--bg-surface-1);
  border: 1px solid var(--border-active);
  border-radius: 10px;
  overflow: hidden;
  margin: 0 1.5rem 1.5rem;
  container-type: inline-size;
  container-name: cbz-list;
}

.cbz-list__batch-bar { height: 44px; display: flex; align-items: center; gap: 1rem; padding: 0 1rem; background: #3b82f6; flex-shrink: 0; }
.cbz-list__batch-count { color: #ffffff; font-family: var(--font-body); font-size: 13px; font-weight: 500; }
.cbz-list__batch-action { background: none; border: none; border-left: 1px solid rgba(255,255,255,0.3); padding-left: 1rem; color: #ffffff; font-family: var(--font-body); font-size: 12px; cursor: pointer; }
.cbz-list__batch-clear { margin-left: auto; background: none; border: none; color: #ffffff; cursor: pointer; display: flex; }

.cbz-list__col-header {
  display: grid;
  grid-template-columns: 32px minmax(0, 28fr) minmax(0, 14fr) minmax(0, 12fr) minmax(0, 14fr) minmax(0, 10fr) minmax(0, 10fr) 40px;
  column-gap: 0.5rem;
  height: 36px;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
}

.cbz-list__col--sortable {
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  font-family: var(--font-body);
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #94a3b8;
  padding: 0 0.4rem;
  display: flex;
  align-items: center;
}

.cbz-list__col--lastpay {
  font-family: var(--font-body);
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #94a3b8;
  display: flex;
  align-items: center;
  padding: 0 0.4rem;
}
.cbz-list__filter-btn { background: none; border: none; color: #94a3b8; cursor: pointer; display: flex; margin: 0 auto; }
.cbz-list__filter-dropdown { position: absolute; top: 100%; left: 0; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px; box-shadow: 0 8px 24px rgba(15,23,42,0.12); padding: 0.4rem; z-index: 50; min-width: 170px; }
.cbz-list__filter-option { display: flex; align-items: center; gap: 0.4rem; font-family: var(--font-body); font-size: 12px; color: var(--text-content); padding: 0.3rem 0.4rem; cursor: pointer; }
.cbz-list__filter-dot { width: 8px; height: 8px; border-radius: 50%; }

.cbz-list__body { flex: 1; min-height: 0; overflow-y: auto; }
.cbz-list__body::-webkit-scrollbar { width: 6px; }
.cbz-list__body::-webkit-scrollbar-thumb { background: var(--border-active); border-radius: 3px; }
.cbz-list__body::-webkit-scrollbar-track { background: transparent; }

.cbz-list__row {
  display: grid;
  grid-template-columns: 32px minmax(0, 28fr) minmax(0, 14fr) minmax(0, 12fr) minmax(0, 14fr) minmax(0, 10fr) minmax(0, 10fr) 40px;
  column-gap: 0.5rem;
  height: 52px;
  border-bottom: 1px solid #f1f5f9;
  background: #ffffff;
  cursor: pointer;
  transition: background 80ms ease;
}

.cbz-list__row--even { background: #fafafa; }
.cbz-list__row--overdue-severe { background: #fff5f5 !important; }
.cbz-list__row:hover { background: #eff6ff; }
.cbz-list__row--overdue-severe:hover { background: #fef2f2; }
.cbz-list__row--selected { background: #eff6ff; }

.cbz-list__col { display: flex; align-items: center; padding: 0 0.4rem; min-width: 0; overflow: hidden; position: relative; }
.cbz-list__col--status { justify-content: center; }

.cbz-list__dot { width: 10px; height: 10px; border-radius: 50%; }
.cbz-list__dot--green { background: #10b981; }
.cbz-list__dot--blue { background: #3b82f6; }
.cbz-list__dot--amber { background: #f0a500; }
.cbz-list__dot--orange { background: #f97316; }
.cbz-list__dot--red { background: #e84545; }

.cbz-list__col--tenant { flex-direction: column; align-items: flex-start; justify-content: center; gap: 0.1rem; min-width: 0; }
.cbz-list__tenant-name-row { display: flex; align-items: center; gap: 0.3rem; max-width: 100%; }
.cbz-list__tenant-name { font-family: var(--font-body); font-size: 13px; font-weight: 500; color: #1e3a5f; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cbz-list__reminder-icon { color: #64748b; font-size: 12px; flex-shrink: 0; }
.cbz-list__plot-tag { font-size: 10px; background: #f1f5f9; color: #64748b; border-radius: 4px; padding: 0.02rem 0.3rem; }

.cbz-list__tooltip { position: absolute; top: 100%; left: 0.4rem; z-index: 60; background: #1e3a5f; color: #ffffff; font-size: 11px; padding: 0.4rem 0.6rem; border-radius: 6px; white-space: nowrap; box-shadow: 0 4px 12px rgba(0,0,0,0.2); }
.cbz-list__tooltip-rfc { color: rgba(255,255,255,0.7); margin-top: 0.15rem; }
.cbz-list__tooltip--breakdown { white-space: normal; min-width: 150px; }

.cbz-list__col--charges { font-family: var(--font-mono); font-size: 13px; color: #64748b; }
.cbz-list__col--paid { font-family: var(--font-mono); font-size: 13px; color: #64748b; }
.cbz-list__paid--green { color: #10b981; }
.cbz-list__paid--amber { color: #f0a500; }
.cbz-list__paid--red { color: #e84545; }

.cbz-list__col--pending { flex-direction: column; align-items: flex-start; justify-content: center; }
.cbz-list__pending-main { font-family: var(--font-mono); font-size: 13px; font-weight: 700; }
.cbz-list__pending-main--zero { font-family: var(--font-body); font-weight: 500; color: #10b981; }
.cbz-list__pending-main--green { color: #10b981; }
.cbz-list__pending-main--blue { color: #3b82f6; }
.cbz-list__pending-main--amber { color: #f0a500; }
.cbz-list__pending-main--orange { color: #f97316; }
.cbz-list__pending-main--red { color: #e84545; }
.cbz-list__pending-prior { font-family: var(--font-body); font-size: 10px; color: #dc2626; }

.cbz-list__col--due { font-family: var(--font-body); font-size: 12px; }
.cbz-list__due--navy { color: #1e3a5f; }
.cbz-list__due--amber { color: #d97706; }
.cbz-list__due--red { color: #dc2626; font-weight: 500; }

.cbz-list__col--actions { justify-content: center; }
.cbz-list__menu-btn { background: none; border: none; color: #94a3b8; cursor: pointer; display: flex; }

.cbz-list__action-menu { position: fixed; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px; box-shadow: 0 8px 24px rgba(15,23,42,0.12); min-width: 220px; z-index: 1000; padding: 0.3rem; }
.cbz-list__action-item { display: flex; align-items: center; gap: 0.4rem; width: 100%; text-align: left; background: none; border: none; font-family: var(--font-body); font-size: 12px; color: var(--text-content); padding: 0.4rem 0.5rem; cursor: pointer; border-radius: 5px; }
.cbz-list__action-item:hover { background: #f8fafc; }
.cbz-list__action-dot { width: 6px; height: 6px; border-radius: 50%; background: #3b82f6; flex-shrink: 0; }

.cbz-list__footer { height: 44px; display: flex; align-items: center; padding: 0 1rem; background: #f8f9fc; border-top: 1px solid #e2e8f0; flex-shrink: 0; }
.cbz-list__footer-count { font-family: var(--font-body); font-size: 12px; color: #64748b; }
.cbz-list__footer-right { margin-left: auto; display: flex; gap: 1.1rem; }
.cbz-list__stat { position: relative; display: inline-flex; align-items: baseline; gap: 0.35rem; cursor: help; outline: none; }
.cbz-list__stat-label { font-family: var(--font-body); font-size: 11px; color: #94a3b8; }
.cbz-list__stat-tip { position: absolute; bottom: calc(100% + 8px); right: 0; width: 250px; background: #1e3a5f; color: #fff; border-radius: 8px; padding: 0.55rem 0.7rem; font-family: var(--font-body); font-size: 11px; line-height: 1.4; box-shadow: 0 8px 24px rgba(15,23,42,0.2); opacity: 0; visibility: hidden; transform: translateY(2px); transition: opacity 0.12s ease, transform 0.12s ease; pointer-events: none; z-index: 20; }
.cbz-list__stat-tip strong { display: block; font-size: 11px; margin-bottom: 0.15rem; }
.cbz-list__stat:hover .cbz-list__stat-tip, .cbz-list__stat:focus .cbz-list__stat-tip { opacity: 1; visibility: visible; transform: none; }
.cbz-list__footer-total { font-family: var(--font-mono); font-size: 12px; color: #1e3a5f; }
.cbz-list__footer-total--green { color: #16a34a; }
.cbz-list__footer-total--amber { color: #d97706; }
.cbz-list__footer-total--red { color: #dc2626; }
.cbz-list__footer-total--gray { color: #94a3b8; }
.cbz-list__col {
  display: flex;
  align-items: center;
  padding: 0 0.4rem;
  min-width: 0;
  overflow: hidden;
  position: relative;
}

.cbz-list__col--tenant {
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 0.1rem;
  min-width: 0;
}

.cbz-list__tenant-name {
  font-family: var(--font-body);
  font-size: 13px;
  font-weight: 500;
  color: #1e3a5f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  min-width: 0;
}

.cbz-list__col--pending {
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  min-width: 0;
}

.cbz-list__pending-main {
  font-family: var(--font-mono);
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  min-width: 0;
}
@container cbz-list (max-width: 950px) {
  .cbz-list .cbz-list__col-header,
  .cbz-list .cbz-list__row {
    grid-template-columns: 32px minmax(0, 30fr) minmax(0, 14fr) minmax(0, 12fr) minmax(0, 16fr) minmax(0, 10fr) 40px;
  }
  .cbz-list .cbz-list__col--lastpay {
    display: none;
  }
}

@container cbz-list (max-width: 700px) {
  .cbz-list .cbz-list__col-header,
  .cbz-list .cbz-list__row {
    grid-template-columns: 32px minmax(0, 36fr) minmax(0, 16fr) minmax(0, 20fr) minmax(0, 14fr) 40px;
    column-gap: 0.35rem;
  }
  .cbz-list .cbz-list__col--paid {
    display: none;
  }
}
.cbz-list__col-header .cbz-list__col--sortable,
.cbz-list__col-header .cbz-list__col--lastpay {
  font-family: var(--font-body);
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #94a3b8;
}

.cbz-list__col--lastpay { flex-direction: column; align-items: flex-start; justify-content: center; gap: 0.1rem; }
.cbz-list__commitment { display: block; font-family: var(--font-body); font-size: 9px; color: #3b82f6; }
.cbz-notice { display: flex; align-items: flex-start; gap: 0.5rem; margin: 0 1.5rem 0.5rem; padding: 0.5rem 0.75rem; border-radius: 8px; background: #fffbeb; border: 1px solid #fde68a; color: #92400e; font-family: var(--font-body); font-size: 12px; line-height: 1.4; }
.cbz-notice__text { flex: 1; min-width: 0; }
.cbz-notice__close { background: none; border: none; color: inherit; cursor: pointer; display: flex; padding: 0; }
</style>